import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, Plus, Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Checkbox } from "@/components/ui/Checkbox";

const DROPDOWN_MAX_HEIGHT = 192;
const DROPDOWN_OFFSET = 4;
const HIDDEN_DROPDOWN_STYLE = { position: "fixed", visibility: "hidden" };

// `scroll` does not bubble, so a listener on `window` only sees it during the
// capture phase — which some embeddings (an Electron webview among them)
// deliver late enough that the trigger has already scrolled well out from
// under the list before it fires, leaving a detached box on screen for
// several frames. Binding straight to the trigger's own scrollable ancestors
// re-anchors it the instant that container moves, with the window listener
// kept as a catch-all for any other scroll source.
const getScrollParents = (el) => {
  const parents = [];
  let node = el?.parentElement;

  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    // `overflow-y-auto` is a precaution on plenty of containers that are not
    // currently overflowing anything — a modal's content wrapper among them,
    // sized to fit a couple of fields rather than stretched to the dialog's
    // full height. Treating it as THE bounding panel anyway clamped a
    // country picker's dropdown to that wrapper's own barely-field-sized
    // rect, leaving it a couple of pixels tall with the option list
    // functionally invisible. `scrollHeight > clientHeight` asks whether
    // this ancestor is actually clipping something right now, which is the
    // one case its bounds should constrain anything.
    if (/(auto|scroll)/.test(style.overflowY + style.overflow) && node.scrollHeight > node.clientHeight) {
      parents.push(node);
    }
    node = node.parentElement;
  }

  return parents;
};

// The nearest scrollable ancestor — the form panel the trigger actually
// scrolls inside, not the dialog card around it. Bounding the list to the
// *card* (its `overflow-hidden` wrapper) still let the list sit on top of
// the card's own header and footer, which live outside this panel as its
// non-scrolling siblings: a trigger scrolled toward the top of the panel
// re-anchored the list right over the header, hiding the title and the close
// button. Bounding to the panel itself excludes both by construction, since
// neither one is inside it.
const getScrollPanel = (el) => getScrollParents(el)[0] ?? null;

/**
 * The app's one dropdown. Trigger, portalled list, optional search box.
 *
 * `RHFSelect` wraps this for react-hook-form — it is the same control, with a
 * label, a taller trigger and the field error. There is no second copy.
 *
 * @param {Array<{label: string, value: any, icon?: Function}>} options
 * @param {string} [fallbackLabel] shown when `value` names no option in the list
 * @param {string} [id]            ties a `<label htmlFor>` to the trigger
 * @param {boolean} [creatable]    with `onCreate`, a typed term that matches no
 *                                 option exactly offers an `Add "<term>"` row
 * @param {(term: string) => void} [onCreate] called with the trimmed term when
 *                                 the add row is committed
 * @param {string} [triggerLabel] what the closed trigger says, in place of the
 *                                 selection — for a caller that shows the
 *                                 selection elsewhere (RHFSelect's `chips`)
 * @param {boolean} [multiple]     `value` is an array, `onChange` gets the next
 *                                 array, a pick toggles and the list STAYS
 *                                 OPEN. There is no `maxSelected`: a caller
 *                                 that wants a cap marks the remaining options
 *                                 `disabled`, so the row still shows and says
 *                                 why rather than vanishing.
 */
const Select = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  className,
  id,
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search...",
  fallbackLabel,
  hasError = false,
  creatable = false,
  onCreate,
  multiple = false,
  triggerLabel,
  // Optional trailing control per option, e.g. an eye that previews what the
  // option means without selecting it. Given the option, returns a node.
  // Its own click must not select the row, so the wrapper stops propagation —
  // a caller does not have to remember to.
  optionAction,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  onClick: onTriggerClick,
  onKeyDown: onTriggerKeyDown,
  ...triggerProps
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState(HIDDEN_DROPDOWN_STYLE);
  const [searchTerm, setSearchTerm] = useState("");

  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxId = useId();

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options;

    const keyword = searchTerm.trim().toLowerCase();

    return options.filter((option) => String(option.label).toLowerCase().includes(keyword));
  }, [options, searchTerm, searchable]);

  // The add row appears only when the term names nothing in the FULL list —
  // not merely nothing in the filtered one, or a shared prefix would hide it
  // (a tenant offering "Hindi" must still be able to add "Hindustani").
  const trimmedTerm = searchTerm.trim();
  const showCreateRow = creatable && !!onCreate && !!trimmedTerm && !options.some((option) => String(option.label).trim().toLowerCase() === trimmedTerm.toLowerCase());
  // The keyboard treats the add row as one slot past the filtered options.
  const createSlotIndex = showCreateRow ? filteredOptions.length : null;
  const totalRows = filteredOptions.length + (showCreateRow ? 1 : 0);

  const commitCreate = () => {
    onCreate?.(trimmedTerm);
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setActiveIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [open, searchable]);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Bound to the scroll panel the trigger sits in, not the raw viewport —
    // falling back to the viewport itself outside one. `boundsTop` /
    // `boundsBottom` are where the list is allowed to reach; `triggerTop` /
    // `triggerBottom` clamp the trigger's own edges into that range, so a
    // trigger that has scrolled past the panel's edge still anchors the list
    // AT that edge instead of past it. That clamp is what keeps the list
    // riding inside the panel through a scroll instead of drawing over the
    // dialog's header or footer, which sit outside it.
    const panel = getScrollPanel(triggerRef.current);
    const panelRect = panel?.getBoundingClientRect();
    const boundsTop = (panelRect?.top ?? 0) + DROPDOWN_OFFSET;
    const boundsBottom = (panelRect?.bottom ?? viewportHeight) - DROPDOWN_OFFSET;

    const triggerTop = Math.min(Math.max(rect.top, boundsTop), boundsBottom);
    const triggerBottom = Math.min(Math.max(rect.bottom, boundsTop), boundsBottom);

    const spaceBelow = boundsBottom - triggerBottom;
    const spaceAbove = triggerTop - boundsTop;

    const dropdownHeight = Math.min(listRef.current?.scrollHeight || 0, DROPDOWN_MAX_HEIGHT);

    const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownStyle({
      position: "fixed",
      visibility: "visible",
      left: rect.left,
      // At least the trigger's width, and wider when an option needs it, up
      // to the viewport's right edge: a narrow trigger (a table cell's) cut
      // its own options short when the list matched it exactly.
      minWidth: rect.width,
      maxWidth: Math.max(rect.width, window.innerWidth - rect.left - DROPDOWN_OFFSET),
      zIndex: 9999,
      // A modal Radix dialog (AlertDialog, always; Dialog when `modal`) sets
      // `document.body.style.pointerEvents = "none"` while open, restoring it
      // only on its own Content node — not on this list, which portals to
      // `document.body` directly and is therefore a sibling, not a descendant.
      // Without this override the list still renders but nothing in it can
      // be clicked. A no-op everywhere else, since `auto` is already the
      // default outside a pointer-events-disabled ancestor.
      pointerEvents: "auto",
      maxHeight: Math.max(0, Math.min(DROPDOWN_MAX_HEIGHT, openUpward ? spaceAbove : spaceBelow)),
      ...(openUpward
        ? {
            bottom: viewportHeight - triggerTop + DROPDOWN_OFFSET,
          }
        : {
            top: triggerBottom + DROPDOWN_OFFSET,
          }),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) calculatePosition();
  }, [open, calculatePosition]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (!triggerRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);

    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleScroll = (e) => {
      // Scrolling the list itself — to reach an option below the fold — is
      // the reader using the dropdown, not leaving it, and it already tracks
      // the trigger (nothing moved), so there's nothing to recompute.
      if (listRef.current?.contains(e.target)) return;

      // While the trigger is still at least partly visible in its scroll
      // panel, re-anchor the list to it instead of closing — closing on
      // every pixel of scroll made scrolling to read the rest of a long form
      // silently throw away a half-made choice. `calculatePosition` clamps
      // that anchor to the panel's own bounds, so the list rides along
      // inside it rather than drawing over the dialog's header or footer.
      //
      // Once the trigger scrolls fully out of the panel, there is no longer
      // a visible field for the list to belong to — clamping it to the
      // panel edge at that point just pins a detached box on top of whatever
      // OTHER content has scrolled up underneath it. Closing here is the
      // only option that does not look like a stray box floating over the
      // wrong row.
      const trigger = triggerRef.current;

      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        const panel = getScrollPanel(trigger);
        const panelRect = panel?.getBoundingClientRect();
        const visibleTop = panelRect?.top ?? 0;
        const visibleBottom = panelRect?.bottom ?? window.innerHeight;
        const triggerVisible = rect.bottom > visibleTop && rect.top < visibleBottom;

        if (!triggerVisible) {
          setOpen(false);
          return;
        }
      }

      calculatePosition();
    };

    const scrollParents = getScrollParents(triggerRef.current);

    window.addEventListener("scroll", handleScroll, true);
    scrollParents.forEach((parent) => parent.addEventListener("scroll", handleScroll));
    window.addEventListener("resize", calculatePosition);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      scrollParents.forEach((parent) => parent.removeEventListener("scroll", handleScroll));
      window.removeEventListener("resize", calculatePosition);
    };
  }, [open, calculatePosition]);

  // One helper every value comparison goes through, so the scalar and the
  // array cases cannot drift apart: the highlight, the check mark, the
  // `aria-selected` and the trigger label all ask the same question.
  const selectedValues = useMemo(() => (multiple ? (Array.isArray(value) ? value : []) : []), [multiple, value]);
  const isSelected = useCallback((optionValue) => (multiple ? selectedValues.includes(optionValue) : optionValue === value), [multiple, selectedValues, value]);

  const selected = multiple ? selectedValues.length > 0 : options.find((o) => o.value === value);
  // The create row sits past the end of `filteredOptions`, so it needs its
  // own bounds here too — otherwise `aria-activedescendant` goes blank the
  // moment the highlight reaches it.
  const activeOptionId = activeIndex >= 0 && activeIndex < totalRows ? `${listboxId}-option-${activeIndex}` : undefined;
  const hasFallbackValue = value !== undefined && value !== null && value !== "" && fallbackLabel;

  // Past a couple of picks the joined labels outgrow the trigger and truncate
  // to something unreadable, so the count says more than half a label would.
  const multipleLabel = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length > 2) return `${selectedValues.length} selected`;
    return selectedValues.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");
  }, [options, placeholder, selectedValues]);

  const displayValue = triggerLabel ?? (multiple ? multipleLabel : selected ? selected.label : hasFallbackValue ? fallbackLabel : placeholder);

  const getInitialActiveIndex = useCallback(
    (preferredIndex) => {
      if (totalRows === 0) return -1;

      if (typeof preferredIndex === "number") {
        return Math.max(0, Math.min(preferredIndex, totalRows - 1));
      }

      const selectedIndex = filteredOptions.findIndex((option) => isSelected(option.value));

      return selectedIndex >= 0 ? selectedIndex : 0;
    },
    [filteredOptions, isSelected, totalRows],
  );

  const openDropdown = useCallback(
    (preferredIndex) => {
      if (disabled) return;

      setActiveIndex(getInitialActiveIndex(preferredIndex));
      setOpen(true);
    },
    [disabled, getInitialActiveIndex],
  );

  // A row a caller marked unavailable stays in the list and says why (that is
  // the point of `disabled` on an option: dropping the row instead leaves the
  // reader searching for something silently absent), so every path that could
  // choose one has to refuse it — the click, and Enter via selectActiveOption.
  // The create row sits past the end of `filteredOptions`, so it reads as
  // enabled and takes the highlight like any other row.
  const moveActiveOption = useCallback(
    (direction) => {
      if (totalRows === 0) return;

      setActiveIndex((currentIndex) => {
        const startIndex = currentIndex < 0 ? (direction > 0 ? -1 : 0) : currentIndex;
        let next = startIndex;

        // Skips rows a caller marked unavailable, wrapping, and gives up
        // after a full lap so a list where every row is disabled stops
        // rather than spins.
        for (let n = 0; n < totalRows; n += 1) {
          next = (next + direction + totalRows) % totalRows;
          if (next >= filteredOptions.length || !filteredOptions[next]?.disabled) return next;
        }
        return currentIndex;
      });
    },
    [filteredOptions, totalRows],
  );

  const selectOption = useCallback(
    (option) => {
      if (!option || option.disabled) return;

      // Multi-select keeps the list open: choosing four keywords is one
      // gesture, not four reopenings. Escape, an outside click and Tab still
      // close it, so there is no way to get stuck in the list.
      if (multiple) {
        const next = selectedValues.includes(option.value) ? selectedValues.filter((v) => v !== option.value) : [...selectedValues, option.value];
        onChange?.(next);
        return;
      }

      onChange?.(option.value);
      setOpen(false);
    },
    [multiple, onChange, selectedValues],
  );

  const selectActiveOption = useCallback(() => {
    if (createSlotIndex !== null && activeIndex === createSlotIndex) {
      commitCreate();
      return;
    }
    selectOption(filteredOptions[activeIndex]);
  }, [activeIndex, createSlotIndex, commitCreate, filteredOptions, selectOption]);

  const getAdjacentInitialIndex = useCallback(
    (direction) => {
      if (filteredOptions.length === 0) return -1;

      const selectedIndex = filteredOptions.findIndex((option) => isSelected(option.value));

      if (selectedIndex < 0) {
        return direction > 0 ? 0 : filteredOptions.length - 1;
      }

      return (selectedIndex + direction + filteredOptions.length) % filteredOptions.length;
    },
    [filteredOptions, isSelected],
  );

  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  /**
   * `fromSearch` marks events that bubbled up from the search box. There,
   * Home/End/Space belong to the text being typed — moving the caret or
   * inserting a space, the way they do in every other text field — not to
   * the highlighted row. Only Escape, Enter and the arrow keys are shared
   * with the list.
   */
  const handleListKeyDown = useCallback(
    (event) => {
      const fromSearch = event.target === searchInputRef.current;

      if (fromSearch) {
        switch (event.key) {
          case "Escape":
            event.preventDefault();
            closeAndFocusTrigger();
            break;
          case "Enter":
            event.preventDefault();
            selectActiveOption();
            break;
          case "ArrowDown":
            event.preventDefault();
            moveActiveOption(1);
            break;
          case "ArrowUp":
            event.preventDefault();
            moveActiveOption(-1);
            break;
          case "Tab":
            setOpen(false);
            break;
          default:
            break;
        }
        return;
      }

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          closeAndFocusTrigger();
          break;
        case "Enter":
        case " ":
        case "Spacebar":
          event.preventDefault();
          selectActiveOption();
          break;
        case "ArrowDown":
          event.preventDefault();
          moveActiveOption(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          moveActiveOption(-1);
          break;
        case "Home":
          event.preventDefault();
          setActiveIndex(getInitialActiveIndex(0));
          break;
        case "End":
          event.preventDefault();
          setActiveIndex(getInitialActiveIndex(totalRows - 1));
          break;
        case "Tab":
          // Leave the field the way Tab always does, but do not leave the
          // list painted over the page behind it.
          setOpen(false);
          break;
        default:
          break;
      }
    },
    [closeAndFocusTrigger, getInitialActiveIndex, moveActiveOption, selectActiveOption, totalRows],
  );

  const handleTriggerClick = useCallback(
    (event) => {
      onTriggerClick?.(event);

      if (event.defaultPrevented || disabled) return;

      if (open) {
        setOpen(false);
      } else {
        openDropdown();
      }
    },
    [disabled, onTriggerClick, open, openDropdown],
  );

  const handleTriggerKeyDown = useCallback(
    (event) => {
      onTriggerKeyDown?.(event);

      if (event.defaultPrevented || disabled) return;

      switch (event.key) {
        case "Escape":
          if (open) {
            event.preventDefault();
            closeAndFocusTrigger();
          }
          break;
        case "Enter":
        case " ":
        case "Spacebar":
          event.preventDefault();
          if (open) {
            selectActiveOption();
          } else {
            openDropdown();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (open) {
            moveActiveOption(1);
          } else {
            openDropdown(getAdjacentInitialIndex(1));
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (open) {
            moveActiveOption(-1);
          } else {
            openDropdown(getAdjacentInitialIndex(-1));
          }
          break;
        case "Home":
          event.preventDefault();
          if (open) {
            setActiveIndex(getInitialActiveIndex(0));
          } else {
            openDropdown(0);
          }
          break;
        case "End":
          event.preventDefault();
          if (open) {
            setActiveIndex(getInitialActiveIndex(filteredOptions.length - 1));
          } else {
            openDropdown(filteredOptions.length - 1);
          }
          break;
        case "Tab":
          if (open) {
            setOpen(false);
          }
          break;
        default:
          break;
      }
    },
    [
      closeAndFocusTrigger,
      disabled,
      filteredOptions.length,
      getAdjacentInitialIndex,
      getInitialActiveIndex,
      moveActiveOption,
      onTriggerKeyDown,
      open,
      openDropdown,
      selectActiveOption,
    ],
  );

  useEffect(() => {
    if (!open) return;

    setActiveIndex((currentIndex) => {
      if (totalRows === 0) return -1;
      // The create row sits past the end of `filteredOptions`, so it is
      // still "in bounds" here — otherwise every keystroke that keeps it
      // showing would silently knock the highlight off it.
      if (currentIndex >= 0 && currentIndex < totalRows) {
        return currentIndex;
      }

      return getInitialActiveIndex();
    });
  }, [filteredOptions, getInitialActiveIndex, open, totalRows]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;

    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, filteredOptions, open]);

  return (
    // The width a caller asks for (`sm:w-56`, `max-w-xs`, `flex-1`...) is
    // applied to the trigger Button below, but this outer div is what a
    // flex/grid sibling actually sizes against. Without the same className
    // here, this wrapper stays plain `w-full` — its flex-basis — while the
    // visually narrower button floats inside it, so a Select sitting next to
    // another flex item (e.g. SearchInput) claims the whole row and
    // overlaps its neighbour instead of sharing space with it.
    //
    // `className` also carries the trigger's own padding/height/background
    // (every RHFSelect passes `px-4 py-2.5 ... bg-background-form/40`), and
    // this wrapper has no border of its own to justify repainting or
    // re-padding — it did anyway, insetting the visible button inside a
    // second tinted padding ring that a plain `Input` never has. `p-0
    // bg-transparent h-auto` after `className` cancels just that, leaving
    // any width utility (the only thing this div is for) in effect.
    <div className={cn("relative w-full", className, "h-auto bg-transparent p-0")}>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        disabled={disabled}
        {...triggerProps}
        id={id}
        role="combobox"
        aria-label={ariaLabel || placeholder}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open ? activeOptionId : undefined}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          // The form-field surface, from the app's own tokens rather than the
          // shadcn `outline` variant's --background / --muted / --foreground:
          // a Select sits in a form next to `Input`, and this is what `Input`
          // is painted with.
          // `h-10 py-2.5`, not the `default` size's `h-8` — a Select sits next
          // to `Input` in every form, and `Input` is `py-2.5` with no fixed
          // height (so it grows to ~42px), which left the trigger visibly
          // shorter than the field beside it.
          "h-10 w-full justify-between border-border-input bg-background-form py-2.5",
          // The focus ring, likewise from `Input` — the two controls share a
          // row in every form, so they light up the same way.
          "focus-visible:border-tone-blue focus-visible:ring-2 focus-visible:ring-tone-blue/40",
          "hover:bg-background-tertiary hover:text-text-primary",
          "aria-expanded:bg-background-tertiary aria-expanded:text-text-primary",
          selected || hasFallbackValue ? "text-text-primary" : "text-text-muted",
          hasError && "border-destructive",
          className,
        )}
      >
        <span className="truncate">{displayValue}</span>

        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </Button>

      {open &&
        createPortal(
          <div
            ref={listRef}
            style={dropdownStyle}
            className="flex flex-col overflow-hidden rounded-lg border border-border-primary bg-background-secondary text-text-primary shadow-lg"
            onKeyDown={handleListKeyDown}
          >
            {searchable && (
              <div className="sticky top-0 flex items-center gap-2 border-b border-border-primary bg-background-secondary p-2">
                <Search size={14} className="shrink-0 text-text-secondary" />

                <Input
                  ref={searchInputRef}
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  role="searchbox"
                  aria-label={`Filter ${ariaLabel || placeholder} options`}
                  aria-controls={listboxId}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="border-0 bg-transparent px-0 py-0 shadow-none focus:border-transparent text-xs"
                />
              </div>
            )}

            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable={multiple || undefined}
              aria-label={`${ariaLabel || placeholder} options`}
              aria-labelledby={ariaLabelledby}
              /* Height comes from the portalled container, which the
                 positioning effect sized against the real space under the
                 trigger. This carried its own `maxHeight: 160` — always the
                 smaller of the two, so it always won and that measurement
                 never decided anything. A four-option list rendered ~144px of
                 options into a 160px box and scrolled for the remainder,
                 inside panels that already scroll. One cap, applied where the
                 space is measured. */
              className="flex-1 overflow-y-auto custom-scrollbar p-1"
            >
              {filteredOptions.length === 0 && !showCreateRow ? (
                <div className="px-4 py-2 text-sm text-text-muted">No results found</div>
              ) : (
                <>
                  {filteredOptions.map((option, index) => (
                    <div
                      key={`${String(option.value)}-${index}`}
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      id={`${listboxId}-option-${index}`}
                      role="option"
                      disabled={option.disabled}
                      aria-selected={isSelected(option.value)}
                      aria-disabled={option.disabled || undefined}
                      tabIndex={-1}
                      data-active={activeIndex === index ? "true" : undefined}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectOption(option)}
                      className={cn(
                        // `rounded-md` over the list's own `p-1` gutter, so the
                        // hover and active fill reads as a row rather than a
                        // band running edge to edge. `min-h-8` matches the
                        // trigger, which is what stops four short options
                        // looking crushed together in a narrow control.
                        "flex min-h-8 w-full cursor-pointer items-center rounded-md px-3 py-1.5 text-sm font-normal text-text-primary hover:bg-background-tertiary hover:text-text-primary",
                        activeIndex === index ? "bg-background-tertiary" : "bg-transparent",
                      )}
                    >
                      {/* Multi-select: a box per row says at a glance what is
                          picked. Display only — the row is what is clicked, and
                          `aria-selected` is what assistive tech reads — so it is
                          hidden from both and lets the click through. */}
                      {multiple && (
                        <Checkbox
                          checked={isSelected(option.value)}
                          disabled={option.disabled}
                          aria-hidden="true"
                          tabIndex={-1}
                          className="pointer-events-none mr-2"
                        />
                      )}

                      {option.icon && <option.icon size={14} className="mr-2 shrink-0" />}

                      <span className="min-w-0 flex-1 truncate">{option.label}</span>

                      {optionAction && (
                        <span className="ml-2 shrink-0" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                          {optionAction(option)}
                        </span>
                      )}
                    </div>
                  ))}

                  {showCreateRow && (
                    <div
                      ref={(node) => {
                        optionRefs.current[createSlotIndex] = node;
                      }}
                      id={`${listboxId}-option-${createSlotIndex}`}
                      role="option"
                      aria-selected={false}
                      tabIndex={-1}
                      data-active={activeIndex === createSlotIndex ? "true" : undefined}
                      onMouseEnter={() => setActiveIndex(createSlotIndex)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={commitCreate}
                      className={cn(
                        // Matches an option row, plus the divider that marks it
                        // as the one entry that creates rather than chooses.
                        // `mt-1 pt-2` keeps that rule off the row above it now
                        // that the rows are inset.
                        "mt-1 flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-md border-t border-border-primary px-3 py-1.5 pt-2 text-sm font-normal text-text-primary hover:bg-background-tertiary hover:text-text-primary",
                        activeIndex === createSlotIndex ? "bg-background-tertiary" : "bg-transparent",
                      )}
                    >
                      <Plus size={14} className="shrink-0 text-text-secondary" />
                      {`Add "${trimmedTerm}"`}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Select;
